import { USER_ROLES } from './constants'
import { isRecordDeleted } from '../lifecycle/lifecycleRecord'
import { isStaffMapRegionRole } from './visibilityScope'

function sameId (left, right) {
  if (left == null || right == null) return false
  return String(left) === String(right)
}

function isUnrestrictedRole (roleId) {
  return roleId === USER_ROLES.SUPER_ADMIN || roleId === USER_ROLES.ADMINISTRATOR
}

function isAssignedUser (profile, userId) {
  if (sameId(profile?.id, userId)) return true
  return profile?.assignedUserIds?.some(candidate => sameId(candidate, userId)) === true
}

export function canUserReceiveDeviceAssignments (targetUser) {
  if (!targetUser || isRecordDeleted(targetUser)) {
    return false
  }
  const roleId = targetUser.roleId ?? targetUser.role?.id
  return roleId === USER_ROLES.OPERATOR
    || roleId === USER_ROLES.CLIENT
    || roleId === USER_ROLES.API
}

export function canManageUserAssignments (profile) {
  if (!profile) return false
  return isUnrestrictedRole(profile.roleId)
}

export function canAccessUserAssignments (_profile, targetUser) {
  return canUserReceiveDeviceAssignments(targetUser)
}

export function canAccessUserForUpdate (profile, targetUser) {
  if (!profile || !targetUser) return false

  if (isUnrestrictedRole(profile.roleId)) {
    return true
  }

  if (profile.roleId === USER_ROLES.OPERATOR || profile.roleId === USER_ROLES.CLIENT) {
    return isAssignedUser(profile, targetUser.id)
  }

  return false
}

export function canAccessUserForView (profile, targetUser) {
  if (!profile || !targetUser) return false

  if (isUnrestrictedRole(profile.roleId)) {
    return true
  }

  if (sameId(profile.id, targetUser.id)) {
    return true
  }

  if (profile.roleId === USER_ROLES.CLIENT) {
    const targetRoleId = targetUser.roleId ?? targetUser.role?.id
    if (targetRoleId !== USER_ROLES.AGENT) {
      return false
    }
    return isAssignedUser(profile, targetUser.id)
  }

  if (profile.roleId === USER_ROLES.OPERATOR) {
    return isAssignedUser(profile, targetUser.id)
  }

  return false
}

export function canAccessDeviceForView (profile, deviceId) {
  if (!profile || deviceId == null) return false

  if (isUnrestrictedRole(profile.roleId)) {
    return true
  }

  if (
    profile.roleId === USER_ROLES.OPERATOR
    || profile.roleId === USER_ROLES.CLIENT
    || profile.roleId === USER_ROLES.AGENT
  ) {
    return profile.deviceIds?.some(deviceIdCandidate => sameId(deviceIdCandidate, deviceId)) === true
  }

  return false
}

export function canAccessZoneForUpdate (profile, zone) {
  if (!profile || !zone) return false

  if (isUnrestrictedRole(profile.roleId)) {
    return true
  }

  if (profile.roleId === USER_ROLES.CLIENT) {
    return sameId(profile.id, zone.userId)
  }

  if (profile.roleId === USER_ROLES.OPERATOR) {
    if (!zone.userId) return false
    return isAssignedUser(profile, zone.userId)
  }

  return false
}

export function canAccessMapRegionForUpdate (profile, region) {
  if (!profile || !region) return false

  // Client and operator manage only regions they created.
  if (profile.roleId === USER_ROLES.CLIENT || profile.roleId === USER_ROLES.OPERATOR) {
    return sameId(profile.id, region.userId)
  }

  if (isUnrestrictedRole(profile.roleId)) {
    if (region.userId == null) return true
    return isStaffMapRegionRole(region.ownerRoleId)
  }

  if (profile.roleId === USER_ROLES.AGENT) {
    if (!region.userId) return false
    return sameId(profile.id, region.userId)
      || isAssignedUser(profile, region.userId)
  }

  return false
}

function isChatGroupMember (profile, chatGroup) {
  const userIds = chatGroup?.userIds
  if (!profile?.id || !Array.isArray(userIds)) return false
  return userIds.some(userId => sameId(userId, profile.id))
}

export function canAccessChatGroupForUpdate (profile, chatGroup) {
  if (!profile || !chatGroup) return false

  if (isUnrestrictedRole(profile.roleId)) {
    return true
  }

  const createdByUserId = chatGroup.createdByUserId

  // Preferred path: ownership column (API + socket create/list).
  if (createdByUserId) {
    if (
      profile.roleId === USER_ROLES.CLIENT
      || profile.roleId === USER_ROLES.OPERATOR
    ) {
      return sameId(profile.id, createdByUserId)
    }

    if (profile.roleId === USER_ROLES.AGENT) {
      return sameId(profile.id, createdByUserId)
        || isAssignedUser(profile, createdByUserId)
    }

    return false
  }

  // Ownerless legacy rows: API rejects mutate; keep UI View-only (no Edit/Delete).
  return false
}

export function canAccessFamilyForUpdate (profile, family) {
  if (!profile || !family) return false

  if (isUnrestrictedRole(profile.roleId)) {
    return true
  }

  return false
}

/** Client/operator may view families in their visibility scope (find/get only). */
export function canAccessFamilyForView (profile, family, familyId) {
  if (!profile) return false

  if (isUnrestrictedRole(profile.roleId)) {
    return true
  }

  const id = family?.id ?? familyId
  if (id == null || id === '') return false

  if (
    profile.roleId === USER_ROLES.CLIENT
    || profile.roleId === USER_ROLES.OPERATOR
  ) {
    const visibleFamilyIds = profile.visibleFamilyIds ?? [profile.familyId]
    return visibleFamilyIds.some(candidate => sameId(candidate, id))
  }

  return false
}

export function canAccessServiceForView (profile, service) {
  if (!profile || !service) return false

  if (isUnrestrictedRole(profile.roleId)) {
    return true
  }

  if (
    profile.roleId === USER_ROLES.OPERATOR
    || profile.roleId === USER_ROLES.CLIENT
    || profile.roleId === USER_ROLES.AGENT
  ) {
    if (!service.creatorId) return true
    return isAssignedUser(profile, service.creatorId)
  }

  return false
}
